// client/src/pages/Services.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Paper,
  Chip,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ArrowForward,
  Close,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Category as CategoryIcon
} from '@mui/icons-material';
import { allServices, serviceCategories } from '../data/ServicesData';

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedService, setExpandedService] = useState(null);
  const [visibleServices, setVisibleServices] = useState(6);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setSelectedCategory(newCategory);
      setExpandedService(null);
      setVisibleServices(6);
    }
  };

  const handleExpandService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  const loadMore = () => {
    setVisibleServices(prev => prev + 6);
  };

  const filteredServices = selectedCategory === 'all'
    ? allServices
    : allServices.filter(service => service.category === selectedCategory);

  const displayedServices = filteredServices.slice(0, visibleServices);
  const hasMore = visibleServices < filteredServices.length;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
      {/* En-tête */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography 
          variant="overline" 
          sx={{ color: '#ff9800', fontWeight: 600, letterSpacing: 2 }}
          className="fade-in-up"
        >
          Notre Expertise
        </Typography>
        <Typography 
          variant="h2" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            fontSize: { xs: '2rem', md: '3rem' },
          }}
          className="fade-in-up delay-100"
        >
          Services & Prestations
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          maxWidth="700px" 
          margin="auto"
          className="fade-in-up delay-200"
        >
          AUJAA GROUP vous propose une gamme complète de services juridiques, administratifs et prestations.
          Des experts qualifiés à votre service.
        </Typography>
      </Box>

      {/* Filtres par catégorie */}
      <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          value={selectedCategory}
          exclusive
          onChange={handleCategoryChange}
          aria-label="catégories de services"
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            '& .MuiToggleButton-root': {
              borderRadius: '50px !important',
              px: 3,
              py: 1,
              border: '1px solid #e0e0e0',
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #1a237e, #283593)',
                color: 'white',
                borderColor: 'transparent',
              }
            }
          }}
        >
          {serviceCategories.map((cat) => (
            <ToggleButton key={cat.id} value={cat.id}>
              {cat.icon && <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{cat.icon}</Box>}
              {cat.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Grille des services */}
      <Grid container spacing={4}>
        {displayedServices.map((service, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={service.id}>
            <Fade in timeout={index * 100}>
              <Paper
                className="service-card"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Image d'illustration */}
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="service-image"
                    style={{
                      width: '100%',
                      height: 220,
                      objectFit: 'cover',
                    }}
                  />
                  <Chip
                    label={service.category === 'juridique' ? 'Juridique' : 
                           service.category === 'immobilier' ? 'Immobilier' :
                           service.category === 'administratif' ? 'Administratif' :
                           service.category === 'marches' ? 'Marchés Publics' : 'Prestations'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: 'rgba(255,152,0,0.9)',
                      color: '#1a237e',
                      fontWeight: 'bold',
                      borderRadius: 2,
                    }}
                  />
                </Box>

                <Box className="service-content" sx={{ p: 3, flex: 1 }}>
                  <Box className="service-icon" sx={{ mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" className="service-title" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" className="service-description" sx={{ color: '#666', mb: 2 }}>
                    {service.shortDesc}
                  </Typography>

                  <Button
                    className="read-more-btn"
                    onClick={() => handleExpandService(service.id)}
                    sx={{ 
                      color: '#ff9800', 
                      p: 0, 
                      mb: 2,
                      '&:hover': { background: 'transparent', color: '#1a237e' }
                    }}
                  >
                    {expandedService === service.id ? (
                      <>Voir moins <ExpandLess sx={{ fontSize: 18 }} /></>
                    ) : (
                      <>En savoir plus <ExpandMore sx={{ fontSize: 18 }} /></>
                    )}
                  </Button>

                  <div className={`service-details ${expandedService === service.id ? 'expanded' : ''}`}>
                    <Typography variant="body2" sx={{ mb: 2, color: '#555', lineHeight: 1.6 }}>
                      {service.fullDesc}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1 }}>
                      Nos prestations incluent :
                    </Typography>
                    <ul className="feature-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle sx={{ fontSize: 16, color: '#ff9800' }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      component={Link}
                      to={service.link}
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 3,
                        borderRadius: 50,
                        background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                        '&:hover': {
                          background: service.color,
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      Demander ce service <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
                    </Button>
                  </div>
                </Box>
              </Paper>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {/* Bouton Voir plus */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <Button
            variant="outlined"
            onClick={loadMore}
            sx={{
              borderRadius: 50,
              px: 4,
              py: 1.5,
              borderColor: '#1a237e',
              color: '#1a237e',
              '&:hover': {
                borderColor: '#ff9800',
                backgroundColor: 'rgba(255,152,0,0.1)',
              }
            }}
          >
            Voir plus de services <ExpandMore sx={{ ml: 1 }} />
          </Button>
        </Box>
      )}

      {/* Message si aucun service */}
      {filteredServices.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Aucun service disponible dans cette catégorie.
          </Typography>
        </Box>
      )}

      {/* Section CTA après les services */}
      <Paper
        className="animate-on-scroll"
        sx={{
          mt: 6,
          p: { xs: 4, md: 5 },
          background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          borderRadius: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Vous ne trouvez pas ce que vous cherchez ?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, margin: '0 auto', color: '#555' }}>
          Contactez-nous directement, notre équipe saura vous orienter vers la solution adaptée à vos besoins.
        </Typography>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          color="secondary"
          size="large"
          sx={{
            borderRadius: 50,
            px: 4,
            '&:hover': { transform: 'translateY(-3px)' }
          }}
        >
          Nous contacter <ArrowForward sx={{ ml: 1 }} />
        </Button>
      </Paper>
    </Container>
  );
};

export default Services;